import { useEffect, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Menu, X } from "lucide-react";
import Logo from "@/components/vu/Logo";

const links = [{ l: "Services", h: "#services" }, { l: "Process", h: "#process" }, { l: "Pricing", h: "#pricing" }, { l: "FAQ", h: "#faq" }, { l: "Contact", h: "#contact" }];

export default function Nav() {
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        const on = () => setScrolled(window.scrollY > 12);
        window.addEventListener("scroll", on);
        return () => window.removeEventListener("scroll", on);
    }, []);

    useEffect(() => {
        if (open) {
            const prev = document.body.style.overflow;
            document.body.style.overflow = "hidden";
            return () => { document.body.style.overflow = prev; };
        }
    }, [open]);

    // If we land on "/" with a #hash (e.g. after being redirected from another page), scroll to it once mounted.
    useEffect(() => {
        if (location.pathname === "/" && location.hash) {
            setTimeout(() => {
                const el = document.querySelector(location.hash);
                if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
            }, 300);
        }
    }, [location]);

    const handleLink = (h) => (e) => {
        e.preventDefault();
        setOpen(false);
        if (location.pathname !== "/") {
            navigate(`/${h}`);
            return;
        }
        setTimeout(() => {
            const el = document.querySelector(h);
            if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 300);
    };

    return (
        <>
            <header data-testid="site-nav" className={`sticky top-0 z-50 border-b transition-colors ${scrolled ? "bg-[#0E1F16]/95 border-[#1F3A2B] backdrop-blur-md" : "bg-transparent border-transparent"}`}>
                <nav className="max-w-[1180px] mx-auto px-8 h-[72px] flex items-center justify-between">
                    <Link to="/" data-testid="nav-logo" className="flex items-center">
                        <Logo height={44} />
                    </Link>
                    <div className="hidden lg:flex gap-8 text-[14px] font-medium">
                        {links.map((x) => <a key={x.h} href={x.h} onClick={handleLink(x.h)} data-testid={`nav-link-${x.l.toLowerCase()}`} className="vu-link text-[#A6BDB0] hover:text-[#F5F0E8] transition-colors">{x.l}</a>)}
                    </div>
                    <div className="hidden lg:flex items-center gap-4">
                        <a href="#contact" onClick={handleLink("#contact")} data-testid="nav-cta-book" className="vu-btn inline-flex items-center gap-2 bg-[#DFFF3D] text-[#0E1F16] px-5 py-2.5 rounded-full text-[14px] font-semibold warm-glow hover:bg-[#C7E82F] whitespace-nowrap">
                            Free Growth Plan <i className="fa-solid fa-arrow-right text-xs" />
                        </a>
                    </div>
                    <button onClick={() => setOpen(true)} data-testid="nav-mobile-open" className="lg:hidden p-2 text-[#F5F0E8]" aria-label="Open menu"><Menu className="w-6 h-6" /></button>
                </nav>
            </header>

            <div
                data-testid="nav-mobile-overlay"
                aria-hidden={!open}
                className={`fixed inset-0 z-[70] bg-[#0E1F16] lg:hidden flex flex-col transition-opacity duration-200 ease-out ${open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}`}
            >
                <div className="flex justify-between items-center px-8 h-[72px] border-b border-[#1F3A2B]">
                    <Logo height={40} />
                    <button onClick={() => setOpen(false)} data-testid="nav-mobile-close" className="p-2 text-[#F5F0E8]" aria-label="Close menu"><X className="w-6 h-6" /></button>
                </div>
                <div className="flex-1 flex flex-col justify-center px-8 gap-4">
                    {links.map((x, i) => (
                        <a
                            key={x.h}
                            href={x.h}
                            onClick={handleLink(x.h)}
                            data-testid={`nav-mobile-link-${x.l.toLowerCase()}`}
                            className={`font-display text-4xl text-[#F5F0E8] hover:text-[#DFFF3D] transition-all duration-300 ease-out ${open ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}
                            style={{ transitionDelay: open ? `${i * 50 + 60}ms` : "0ms" }}
                        >
                            {x.l}
                        </a>
                    ))}
                    <a
                        href="#contact"
                        onClick={handleLink("#contact")}
                        className={`mt-6 self-start inline-flex items-center gap-2 bg-[#DFFF3D] text-[#0E1F16] px-6 py-3 rounded-full text-[14px] font-semibold transition-all duration-300 ease-out ${open ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}
                        style={{ transitionDelay: open ? `${links.length * 50 + 60}ms` : "0ms" }}
                    >
                        Free Growth Plan <i className="fa-solid fa-arrow-right text-xs" />
                    </a>
                </div>
            </div>
        </>
    );
}